import React, { useState, useEffect } from 'react';
import { Input, Select, SelectOption } from '@flixbus/honeycomb-react';
import { Currencies } from '../../Rules/Trips/Currencies';
import styles from './credit.module.scss';
import { Asterisk } from '../../Asterisk';
import { useVoucherFormContext } from '../../Context';
import parseCurrencyInput from '../../../../helpers/parseCurrencyInput';

const Credit = () => {
  const {
    formState: {
      payload: { discountType },
    },
    handlePayloadChange,
    handleEntityChange,
  } = useVoucherFormContext();

  const isValueMarketing = discountType?.type === 'value_marketing';

  const currentCreditType = isValueMarketing
    ? {
        ...discountType,
        discount: discountType.discount ?? 0,
        currency: discountType.currency ?? 'EUR',
      }
    : {
        ...discountType,
        credit: discountType.credit ?? 0,
        currency: discountType.currency ?? 'EUR',
      };

  const [creditDisplayValue, setCreditDisplayValue] = useState<string>('');

  useEffect(() => {
    const value = isValueMarketing
      ? (currentCreditType.discount ?? 0)
      : (currentCreditType.credit ?? 0);

    if (!currentCreditType.currency) return;

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentCreditType.currency,
      minimumFractionDigits: 2,
    });

    setCreditDisplayValue(formatter.format(value));
  }, [
    currentCreditType.credit,
    currentCreditType.discount,
    currentCreditType.currency,
    currentCreditType.type,
    isValueMarketing,
  ]);

  const handleCreditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCreditDisplayValue(value);
  };

  const handleCreditBlur = () => {
    const numericValue = parseCurrencyInput(creditDisplayValue);

    handlePayloadChange({
      value: {
        ...currentCreditType,
        ...(isValueMarketing ? { discount: numericValue } : { credit: numericValue }),
      },
      prop: 'discountType',
    });

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentCreditType.currency,
      minimumFractionDigits: 2,
    });

    setCreditDisplayValue(formatter.format(numericValue));
  };

  const handleCreditFocus = () => {
    const numericValue = parseCurrencyInput(creditDisplayValue);
    setCreditDisplayValue(numericValue ? numericValue.toString() : '');
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    handlePayloadChange({
      value: {
        ...currentCreditType,
        currency: value,
      },
      prop: 'discountType',
    });
    handleEntityChange(null);
  };

  return (
    <div className={styles.double}>
      <Select
        value={currentCreditType.currency}
        extraClasses={styles.doubleInput}
        id='currency-input'
        required
        placeholder='Currency'
        label={
          <>
            Credit <Asterisk />
          </>
        }
        onChange={handleCurrencyChange}
      >
        {Object.keys(Currencies).map(k => (
          <SelectOption key={k} value={k}>
            {Currencies[k]}
          </SelectOption>
        ))}
      </Select>
      <Input
        extraClasses={styles.doubleInput}
        name='credit'
        id='credit'
        required
        label={<>&nbsp;</>}
        type='text'
        valid={parseCurrencyInput(creditDisplayValue) > 0}
        infoError={'Value must be greater than 0'}
        value={creditDisplayValue}
        onChange={handleCreditChange}
        onBlur={handleCreditBlur}
        onFocus={handleCreditFocus}
      />
    </div>
  );
};

export default Credit;
